/**
 * The contact fields of the ONE details form (docs-company ADR-110
 * Amendment 6, Amendment 8): first name, last name, email and phone,
 * labelled, limited and checked the same way whether the visitor is leaving
 * their details (lead.ts, POST /chat/lead) or giving them for a booking
 * (actions.ts, `collect_details`). DetailsForm.tsx draws both from here.
 *
 * Until Amendment 6 each form kept its own copy, and they drifted: the lead's
 * phone read "Phone (optional)" beside the booking's "Phone *", the lead took
 * an email of 200 characters and the booking one of 254, and a visitor who
 * had just filled in one was handed the other. Pure, so
 * scripts/test-config.mjs pins the rules against the built artefact.
 *
 * 🚨 THE NAME IS TWO BOXES AND ONE VALUE (Amendment 8).
 *
 * The form asks for a first name and a last name; the wire carries the one
 * `name` both backends have always stored — "Ada Lovelace" — joined here by
 * `joinName`. Nothing downstream changes: chat_lead, the notification e-mail,
 * Ema Contacts and the booking core all keep the field they know.
 *
 * What the split buys is the FIRST NAME, exactly. A single box was answered
 * with "Ada", "Ada Lovelace", "Mrs Lovelace" and "lovelace ada", and the
 * assistant's own rule for addressing a visitor (chat_message's
 * `_lead_first_name`) takes the first whitespace token of whatever arrived.
 * With two boxes that token is the first name the visitor typed, so being
 * greeted by name stops being a guess.
 */

export const CONTACT_FIELDS = ['firstName', 'lastName', 'email', 'phone'] as const;

export type ContactField = (typeof CONTACT_FIELDS)[number];

/** Mirrors chat_lead's `_is_valid_email` and conversation_core's `_EMAIL_RE`. */
export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * The tighter of the two backends' limits, so either accepts what the form
 * lets through: chat_lead refuses an email over 200 characters where
 * conversation_core takes 254. A phone is 20 in both, with no format rule.
 *
 * Each half of the name is capped at half of the stored `name`'s 200, and the
 * joined value is checked again below — a limit on each box alone would let
 * two full boxes make a name neither backend would store.
 */
export const CONTACT_LIMITS: Readonly<Record<ContactField, number>> = {
  firstName: 100,
  lastName: 100,
  email: 200,
  phone: 20,
};

/** How long the joined name may be: both backends' `name` limit. */
export const NAME_MAX = 200;

/** The one `name` the wire carries, from the two boxes the form draws. */
export function joinName(firstName = '', lastName = ''): string {
  return [firstName, lastName].map((part) => part.trim()).filter(Boolean).join(' ');
}

export type ContactValues = Partial<Record<ContactField, string>>;

export type ContactValidation =
  | {
      ok: true;
      /** Each field the form showed, trimmed; an empty optional one is left out. */
      value: ContactValues;
      /** The two name boxes as one value, '' when the form showed neither. */
      name: string;
      /** The first name alone, for the words the visitor reads back. */
      firstName: string;
    }
  | { ok: false; field: ContactField; reason: 'required' | 'invalid' | 'tooLong' };

/**
 * Checked in the order the form shows the fields; the first problem wins.
 * Values are trimmed; an optional field left empty is left out, and a field
 * the form did not show is never read. An empty required email is reported
 * as not a valid address, and a joined name over the stored limit is reported
 * against the last name — the box the visitor was typing in when it happened.
 */
export function validateContact(
  raw: ContactValues,
  fields: readonly ContactField[],
  required: readonly ContactField[],
): ContactValidation {
  const value: ContactValues = {};
  for (const field of fields) {
    const v = (raw[field] ?? '').trim();
    if (!v) {
      if (required.includes(field)) return { ok: false, field, reason: field === 'email' ? 'invalid' : 'required' };
      continue;
    }
    if (field === 'email' && !EMAIL_RE.test(v)) return { ok: false, field, reason: 'invalid' };
    if (v.length > CONTACT_LIMITS[field]) return { ok: false, field, reason: 'tooLong' };
    value[field] = v;
  }
  const name = joinName(value.firstName, value.lastName);
  if (name.length > NAME_MAX) {
    return { ok: false, field: fields.includes('lastName') ? 'lastName' : 'firstName', reason: 'tooLong' };
  }
  return { ok: true, value, name, firstName: value.firstName ?? '' };
}
